import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroupDirective, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { SeasonsService } from './seasons.service';
import { ISeason, ISeasonalUserScore } from './seasons.interfaces';
import { SnackBarService } from '../../../../@core/services/snack-bar.service';

@Component({
  selector: 'app-season-score-dialog',
  templateUrl: './season-score-dialog.component.html',
  styles: ['mat-form-field { display: block; } .error { color: #e85b5b; }'],
})
export class SeasonScoreDialogComponent implements OnInit, OnDestroy {
  @ViewChild(FormGroupDirective) private formDirective: FormGroupDirective;
  private readonly destroyed$ = new Subject<void>();
  score: ISeasonalUserScore | null = null;
  loading = false;
  busy = false;
  changed = false;
  error = '';
  readonly fields: {
    key: 'score' | 'pendingScore' | 'completedQuestsCount' | 'rewardsCount';
    label: string;
    hint: string;
    max: number;
  }[] = [
    {
      key: 'score',
      label: 'Собранные очки',
      hint: 'score — очки, которые пользователь уже забрал.',
      max: Number.MAX_SAFE_INTEGER,
    },
    {
      key: 'pendingScore',
      label: 'Незабранные очки',
      hint:
        'pendingScore — заработанные, но ещё не забранные очки, включая целые очки кэшбэка.',
      max: Number.MAX_SAFE_INTEGER,
    },
    {
      key: 'completedQuestsCount',
      label: 'Выполнено квестов',
      hint:
        'completedQuestsCount — выполненные квесты, включая квесты с уже забранными очками.',
      max: 2147483647,
    },
    {
      key: 'rewardsCount',
      label: 'Получено наград за очки',
      hint:
        'rewardsCount — полученные награды за пороги очков. Призовые места сезона сюда не входят.',
      max: 2147483647,
    },
  ];
  readonly form = this.fb.group({
    score: [
      0,
      [
        Validators.required,
        Validators.min(0),
        Validators.max(Number.MAX_SAFE_INTEGER),
        Validators.pattern(/^\d+$/),
      ],
    ],
    pendingScore: [
      0,
      [
        Validators.required,
        Validators.min(0),
        Validators.max(Number.MAX_SAFE_INTEGER),
        Validators.pattern(/^\d+$/),
      ],
    ],
    completedQuestsCount: [
      0,
      [
        Validators.required,
        Validators.min(0),
        Validators.max(2147483647),
        Validators.pattern(/^\d+$/),
      ],
    ],
    rewardsCount: [
      0,
      [
        Validators.required,
        Validators.min(0),
        Validators.max(2147483647),
        Validators.pattern(/^\d+$/),
      ],
    ],
    reason: ['', [Validators.required, Validators.maxLength(1000)]],
  });
  constructor(
    @Inject(MAT_DIALOG_DATA)
    public readonly data: { userId: number; season: ISeason; canEdit: boolean },
    private readonly api: SeasonsService,
    private readonly fb: FormBuilder,
    private readonly snackBar: SnackBarService,
    public readonly ref: MatDialogRef<SeasonScoreDialogComponent>,
  ) {}
  ngOnInit(): void {
    this.load();
  }
  load(): void {
    this.loading = true;
    this.error = '';
    this.api
      .getScore(this.data.userId, this.data.season.id)
      .pipe(
        finalize(() => (this.loading = false)),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: score => {
          this.score = score;
          this.form.patchValue(score);
          if (!this.data.canEdit) this.form.disable();
        },
        error: error => {
          this.score = null;
          this.error =
            error.error?.message || 'Не удалось загрузить очки.';
        },
      });
  }
  get total(): number {
    const value = this.form.getRawValue();
    return Number(value.score) + Number(value.pendingScore);
  }
  /** Отправляем только изменённые поля, чтобы сохранение одного счётчика не перетёрло другие. */
  save(): void {
    if (this.busy || !this.data.canEdit || !this.score || this.form.invalid)
      return;
    const value = this.form.getRawValue();
    const reason = value.reason.trim();
    if (!reason) {
      this.error = 'Укажите причину изменения.';
      return;
    }
    if (!Number.isSafeInteger(this.total)) {
      this.error = 'Итоговое количество очков слишком велико.';
      return;
    }
    const params: Record<string, unknown> = { id: this.score.id, reason };
    for (const field of this.fields)
      if (Number(value[field.key]) !== this.score[field.key])
        params[field.key] = Number(value[field.key]);
    if (Object.keys(params).length === 2) {
      this.error = 'Измените хотя бы один показатель.';
      return;
    }
    this.busy = true;
    this.error = '';
    this.api
      .updateScore(this.data.userId, params)
      .pipe(
        finalize(() => (this.busy = false)),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: score => {
          this.score = score;
          this.changed = true;
          // Сбрасываем также touched/submitted: пустая причина относится уже к следующей коррекции.
          this.formDirective.resetForm({ ...score, reason: '' });
          this.snackBar.openSnackBar('Очки сезона сохранены', 3000, 'success');
        },
        error: () =>
          (this.error =
            'Не удалось сохранить очки. Обновите данные перед повторным сохранением.'),
      });
  }
  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
